<div class="container pricing-content">
  <h1>Pricing at Talespinner.io</h1>

  <section id="intro">
    <p>At Talespinner.io, we believe in transparent pricing. Our goal is to provide you with the best AI-powered writing
      tools at fair prices. Here's how our pricing works:</p>
  </section>

  <section id="quill-system">
    <h2>The Quill System</h2>
    <p>Our pricing is based on Quills, a virtual currency:</p>
    <ul>
      <li>Receive free Quills when you sign up, so you can start writing right away.</li>
      <li>Purchase Quills to pay for AI-powered writing.</li>
      <li>Quills never expire, so use them as you wish.</li>
      <li>One Quill costs one cent (€0.01).</li>
    </ul>
    <p>This simple conversion rate makes it easy to understand your costs: 100 Quills equal €1,00.</p>
    <p>Generating a chapter of a book using AI typically costs between €0,05 and €0,15, depending on the AI model and the amount of context
      provided. See the example below for more details.</p>
  </section>

  <section id="pricing-structure">
    <h2>How Pricing Works</h2>
    <p>Pricing is based on two factors:</p>
    <ul>
      <li><strong>Input Words:</strong> The text you provide as context (e.g., background information, character
        descriptions, previous chapters).
      </li>
      <li><strong>Output Words:</strong> The text generated by the AI.</li>
    </ul>
    <p>The longer your context, the higher the cost of each generation action. Talespinner's prices are directly linked
      to the costs charged by the AI model providers.</p>
  </section>

  <section id="cost-estimation">
    <h2>Cost Estimation</h2>
    <p>Before each generation action, you'll see an estimated price based on:</p>
    <ul>
      <li>The amount of input text (context).</li>
      <li>The expected output text length.</li>
    </ul>
    <p>Final costs may vary slightly, but you'll never be charged more than the upper limit of the estimate.</p>
    <div class="example-box">
      <p>For example, here's a breakdown of Quill costs and word usage for writing a book using the Claude AI
        model:</p>
      <div class="table-responsive">
        <table class="cost-table">
          <thead>
          <tr>
            <th>Action</th>
            <th>Quills</th>
            <th>Input Words</th>
            <th>Output Words</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Writing style generated</td>
            <td>2</td>
            <td colspan="2">N/A</td>
          </tr>
          <tr>
            <td>Background information generated</td>
            <td>2</td>
            <td colspan="2">N/A</td>
          </tr>
          <tr>
            <td>10 chapter summaries generated</td>
            <td>3</td>
            <td colspan="2">N/A</td>
          </tr>
          <tr>
            <td>Chapter 1 text generated</td>
            <td>6</td>
            <td>1726</td>
            <td>1460</td>
          </tr>
          <tr>
            <td>Chapter 2 text generated</td>
            <td>7</td>
            <td>3186</td>
            <td>1492</td>
          </tr>
          <tr>
            <td>Chapter 3 text generated</td>
            <td>7</td>
            <td>4687</td>
            <td>1400</td>
          </tr>
          <tr>
            <td>Chapter 4 text generated</td>
            <td>8</td>
            <td>6078</td>
            <td>1568</td>
          </tr>
          <tr>
            <td>Chapter 5 text generated</td>
            <td>10</td>
            <td>7646</td>
            <td>1841</td>
          </tr>
          <tr>
            <td>Chapter 6 text generated</td>
            <td>11</td>
            <td>9487</td>
            <td>1727</td>
          </tr>
          <tr>
            <td>Chapter 7 text generated</td>
            <td>11</td>
            <td>11214</td>
            <td>1462</td>
          </tr>
          <tr>
            <td>Chapter 8 text generated</td>
            <td>12</td>
            <td>12676</td>
            <td>1500</td>
          </tr>
          <tr>
            <td>Chapter 9 text generated</td>
            <td>13</td>
            <td>14176</td>
            <td>1613</td>
          </tr>
          <tr>
            <td>Chapter 10 text generated</td>
            <td>14</td>
            <td>15789</td>
            <td>1691</td>
          </tr>
          </tbody>
        </table>
      </div>
      <p><strong>Total Quills used: 106</strong></p>
      <p><em>Note: Input words increase with each chapter as more context is included. Actual costs will vary based on
        specific usage, input length, and the chosen AI model.</em></p>
    </div>
  </section>

  <section id="ai-models">
    <h2>AI Model Selection</h2>
    <p>We offer different AI models, each with its own cost structure:</p>
    <ul>
      <li>Choose a model that fits your needs and budget.</li>
      <li>Model prices reflect the costs we incur to access them.</li>
    </ul>
  </section>

  <section id="transparency">
    <h2>Transparency and Control</h2>
    <p>We keep you informed at every step:</p>
    <ul>
      <li>Check your Quill balance anytime.</li>
      <li>View cost estimates before each AI generation.</li>
      <li>Track your usage history.</li>
      <li>No hidden fees or subscriptions.</li>
    </ul>
  </section>
</div>
